import { useQuery } from '@tanstack/react-query'
import type { BookFilters } from '@/components/books/FilterPanel'
import { useAuth } from '@/contexts/auth'
import { supabase } from '@/lib/supabase'
import type { Book, BookType } from '@/types'

interface UseBooksWithFiltersOptions {
  filters: BookFilters & { type?: BookType }
  page?: number
  pageSize?: number
  enabled?: boolean
}

interface BooksResponse {
  books: Book[]
  totalCount: number
  pageCount: number
  currentPage: number
  pageSize: number
}

export function useBooksWithFilters({
  filters,
  page = 1,
  pageSize = 20,
  enabled = true,
}: UseBooksWithFiltersOptions) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['books', user?.id, filters, page, pageSize],
    queryFn: async (): Promise<BooksResponse> => {
      if (!user?.id) throw new Error('User not authenticated')

      // Build the base query
      let query = supabase
        .from('books')
        .select(
          `
          *,
          book_genres (
            genre:genres (
              id,
              name,
              color
            )
          )
        `,
          { count: 'exact' }
        )
        .eq('user_id', user.id)

      // Apply search filter (search in title, author, isbn)
      if (filters.search) {
        const searchTerm = `%${filters.search}%`
        query = query.or(
          `title.ilike.${searchTerm},author.ilike.${searchTerm},isbn.ilike.${searchTerm}`
        )
      }

      // Apply type filter (physical or digital)
      if (filters.type) {
        query = query.eq('type', filters.type)
      }

      // Apply status filters
      if (filters.statuses.length > 0) {
        query = query.in('status', filters.statuses)
      }

      // Apply finished-in-year filter
      if (filters.readYear) {
        query = query
          .gte('end_date', `${filters.readYear}-01-01`)
          .lte('end_date', `${filters.readYear}-12-31`)
      }

      // Apply rating filter
      // Note: This will exclude books with null ratings when min > 0
      if (filters.ratingRange.min > 0) {
        query = query.gte('rating', filters.ratingRange.min)
      }
      if (filters.ratingRange.max < 5) {
        query = query.lte('rating', filters.ratingRange.max)
      }

      // Apply genre filters
      if (filters.genres.length > 0) {
        // First, get book IDs that have the selected genres
        const { data: bookGenres, error: genreError } = await supabase
          .from('book_genres')
          .select('book_id')
          .in('genre_id', filters.genres)

        if (genreError) throw genreError

        const bookIds = [...new Set(bookGenres?.map((bg) => bg.book_id) || [])]

        if (bookIds.length > 0) {
          query = query.in('id', bookIds)
        } else {
          // No books match the genre filter
          return {
            books: [],
            totalCount: 0,
            pageCount: 0,
            currentPage: page,
            pageSize,
          }
        }
      }

      // Apply sorting
      const ascending = filters.sortOrder === 'asc'
      switch (filters.sortBy) {
        case 'title':
          query = query.order('title', { ascending })
          break
        case 'author':
          query = query.order('author', { ascending })
          break
        case 'rating':
          // Handle null ratings by using COALESCE in raw SQL would be ideal,
          // but for now we'll sort by rating with nulls last
          query = query.order('rating', { ascending, nullsFirst: false })
          break
        case 'status':
          query = query.order('status', { ascending })
          break
        default:
          query = query.order('created_at', { ascending })
          break
      }

      // Apply pagination
      const from = (page - 1) * pageSize
      const to = from + pageSize - 1
      query = query.range(from, to)

      // Execute query
      const { data, error, count } = await query

      if (error) throw error

      const totalCount = count || 0
      const pageCount = Math.ceil(totalCount / pageSize)

      return {
        books: data || [],
        totalCount,
        pageCount,
        currentPage: page,
        pageSize,
      }
    },
    enabled: enabled && !!user?.id,
  })
}

// Hook for infinite scroll if needed later
export function useBooksInfinite({
  filters,
  pageSize = 20,
  enabled = true,
}: Omit<UseBooksWithFiltersOptions, 'page'>) {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['books-infinite', user?.id, filters, pageSize],
    queryFn: async (): Promise<BooksResponse> => {
      // This would use the same logic as above but with React Query's infinite query
      // Implementation left as an exercise when needed
      throw new Error('Not implemented yet')
    },
    enabled: enabled && !!user?.id,
  })
}
